import { useState } from "react";
import { AlertOctagon, AlertTriangle, CheckCircle2, Download, Info, Loader2, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Segmented } from "./Fields";

const Stat = ({ label, value, id }) => (
  <div className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0">
    <span className="text-xs text-zinc-400">{label}</span>
    <span className="font-mono text-xs text-white" data-testid={id}>{value}</span>
  </div>
);

const ICON = { error: AlertOctagon, warning: AlertTriangle, info: Info, ok: CheckCircle2 };
const TONE = {
  error: "border-red-500/30 bg-red-500/5 text-red-300",
  warning: "border-amber-500/30 bg-amber-500/5 text-amber-200",
  info: "border-white/10 bg-white/[0.03] text-zinc-300",
  ok: "border-emerald-500/30 bg-emerald-500/5 text-emerald-300",
};

export const InfoPanel = ({ model, onDownload, downloading }) => {
  const [fmt, setFmt] = useState("stl");
  if (!model) {
    return (
      <div className="space-y-6" data-testid="info-panel-empty">
        <div>
          <h3 className="eyebrow mb-3">Model</h3>
          <p className="text-xs text-zinc-500 leading-relaxed">Dimensions, mesh statistics and printability checks appear here after you generate a model.</p>
        </div>
        <div className="rounded-xl border border-white/5 p-4 text-[11px] text-zinc-500 leading-relaxed">
          Free forever: no account, no payment, no watermark. Files are deleted automatically after 24 hours.
        </div>
      </div>
    );
  }
  const s = model.stats;
  const [x, y, z] = s.size_mm;
  return (
    <div className="space-y-8" data-testid="info-panel">
      <section>
        <h3 className="eyebrow mb-3">Dimensions</h3>
        <div className="grid grid-cols-3 gap-2">
          {[["X", x], ["Y", y], ["Z", z]].map(([k, v]) => (
            <div key={k} className="rounded-lg bg-black/40 border border-white/5 p-2.5">
              <p className="text-[10px] text-zinc-500">{k}</p>
              <p className="font-mono text-sm text-white" data-testid={`dim-${k.toLowerCase()}`}>{v}</p>
              <p className="text-[10px] text-zinc-600">mm</p>
            </div>
          ))}
        </div>
      </section>
      <section>
        <h3 className="eyebrow mb-2">Mesh</h3>
        <Stat label="Triangles" value={s.triangles.toLocaleString()} id="stat-triangles" />
        <Stat label="Vertices" value={s.vertices.toLocaleString()} id="stat-vertices" />
        <Stat label="Bounding box" value={`${x} × ${y} × ${z}`} id="stat-bbox" />
        <Stat label="Volume" value={s.volume_cm3 != null ? `${s.volume_cm3} cm³` : "n/a"} id="stat-volume" />
        <Stat label="Watertight" value={s.watertight ? "Yes" : "No"} id="stat-watertight" />
        <Stat label="Open edges" value={s.open_edges} id="stat-open-edges" />
        <Stat label="Degenerate tris" value={s.degenerate_triangles} id="stat-degenerate" />
        <Stat label="Parts" value={s.bodies} id="stat-bodies" />
      </section>
      <section>
        <h3 className="eyebrow mb-3">Printability</h3>
        <div className="space-y-2" data-testid="printability-warnings">
          {model.warnings.map((w, i) => {
            const Icon = ICON[w.level] || Info;
            return (
              <div key={i} className={`flex gap-2 rounded-lg border p-2.5 text-[11px] leading-relaxed ${TONE[w.level]}`} data-testid={`warning-${w.level}`}>
                <Icon size={13} className="shrink-0 mt-0.5" /> {w.message}
              </div>
            );
          })}
          {model.repairs?.length > 0 && (
            <div className="flex gap-2 text-[11px] text-zinc-500"><Wrench size={12} className="mt-0.5" /> Auto-repaired: {model.repairs.join(", ")}</div>
          )}
        </div>
      </section>
      <section className="space-y-3">
        <h3 className="eyebrow">Export</h3>
        <Segmented id="export-format" value={fmt} onChange={setFmt} options={[{ value: "stl", label: "STL" }, { value: "obj", label: "OBJ" }, { value: "glb", label: "GLB" }]} />
        <Button
          data-testid="download-btn"
          disabled={downloading}
          onClick={() => onDownload(fmt)}
          className="w-full h-11 bg-[#ff5a00] hover:bg-[#ff7a33] active:bg-[#e65100] text-white font-semibold transition-[background-color,transform] duration-200 hover:-translate-y-0.5"
        >
          {downloading ? <Loader2 className="animate-spin mr-2" size={16} /> : <Download className="mr-2" size={16} />}
          Download {fmt.toUpperCase()} — Free
        </Button>
        <p className="text-[11px] text-zinc-500">{fmt === "stl" ? "Binary STL in millimeters. Opens in Cura, PrusaSlicer, Bambu Studio." : fmt === "glb" ? "glTF binary for web/AR viewers." : "Wavefront OBJ for modeling tools."} No watermark.</p>
      </section>
    </div>
  );
};
